// useVideoEditor — hook-owns-all-state for the local video editor.
//
// Owns: clip list, object-URL lifecycle, ffmpeg engine load, export job
// (progress + result), and all mutations. The panel is a pure renderer.
// Strictly ephemeral: revokes every object URL on unmount and on reset.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EditorClip,
  EngineState,
  ExportProgress,
  ExportResult,
  RESOLUTION_PRESETS,
  ResolutionPreset,
} from './types';
import { getEngine, onEngineProgress, spliceClips } from './ffmpegClient';

const ACCEPTED = /^video\//i;

let idSeq = 0;
const nextId = () => `clip_${Date.now()}_${idSeq++}`;

/** Best-effort metadata probe via a detached <video> element. */
function probeFile(file: File, url: string): Promise<Partial<EditorClip>> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    const done = (meta: Partial<EditorClip>) => {
      v.removeAttribute('src');
      v.load();
      resolve(meta);
    };
    v.onloadedmetadata = () =>
      done({
        durationSec: Number.isFinite(v.duration) ? v.duration : null,
        width: v.videoWidth || null,
        height: v.videoHeight || null,
      });
    v.onerror = () => done({ durationSec: null, width: null, height: null });
    v.src = url;
  });
}

export function useVideoEditor() {
  const [clips, setClips] = useState<EditorClip[]>([]);
  const [engine, setEngine] = useState<EngineState>('idle');
  const [engineError, setEngineError] = useState<string | null>(null);
  const [preset, setPreset] = useState<ResolutionPreset>(RESOLUTION_PRESETS[0]);
  const [progress, setProgress] = useState<ExportProgress>({
    phase: 'idle',
    ratio: 0,
    label: '',
  });
  const [result, setResult] = useState<ExportResult | null>(null);

  // Mirror of clip URLs + result URL for cleanup without stale closures.
  const urlsRef = useRef<Set<string>>(new Set());
  const resultUrlRef = useRef<string | null>(null);

  const trackUrl = useCallback((url: string) => {
    urlsRef.current.add(url);
  }, []);
  const revokeUrl = useCallback((url: string) => {
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
    urlsRef.current.delete(url);
  }, []);

  // ── Add clips ────────────────────────────────────────────────────────────
  const addFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList).filter((f) => ACCEPTED.test(f.type) || /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(f.name));
      if (files.length === 0) return;

      const staged: EditorClip[] = files.map((file) => {
        const url = URL.createObjectURL(file);
        trackUrl(url);
        return {
          id: nextId(),
          file,
          url,
          name: file.name,
          size: file.size,
          durationSec: null,
          width: null,
          height: null,
        };
      });

      setClips((prev) => [...prev, ...staged]);

      // Probe metadata asynchronously; patch in as it resolves.
      staged.forEach(async (clip) => {
        const meta = await probeFile(clip.file, clip.url);
        setClips((prev) =>
          prev.map((c) => (c.id === clip.id ? { ...c, ...meta } : c)),
        );
      });
    },
    [trackUrl],
  );

  // ── Remove / reorder ───────────────────────────────────────────────────────
  const removeClip = useCallback(
    (id: string) => {
      setClips((prev) => {
        const target = prev.find((c) => c.id === id);
        if (target) revokeUrl(target.url);
        return prev.filter((c) => c.id !== id);
      });
    },
    [revokeUrl],
  );

  const moveClip = useCallback((id: string, dir: -1 | 1) => {
    setClips((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx < 0) return prev;
      const swap = idx + dir;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }, []);

  /** Reorder by moving the clip at `from` to `to` (drag-and-drop). */
  const reorderClip = useCallback((from: number, to: number) => {
    setClips((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  // ── Result lifecycle ───────────────────────────────────────────────────────
  const clearResult = useCallback(() => {
    if (resultUrlRef.current) {
      revokeUrl(resultUrlRef.current);
      resultUrlRef.current = null;
    }
    setResult(null);
  }, [revokeUrl]);

  // ── Export (splice) ────────────────────────────────────────────────────────
  const exporting = progress.phase === 'preparing' || progress.phase === 'encoding' || progress.phase === 'finalizing';

  const exportVideo = useCallback(async () => {
    if (clips.length === 0 || exporting) return;
    clearResult();
    setProgress({ phase: 'preparing', ratio: 0, label: 'Starting engine…' });

    let unsub: (() => void) | undefined;
    try {
      // Lazy-load ffmpeg on first export.
      setEngine((s) => (s === 'ready' ? s : 'loading'));
      const ffmpeg = await getEngine();
      setEngine('ready');
      setEngineError(null);

      unsub = onEngineProgress(ffmpeg, (ratio) => {
        setProgress((p) =>
          p.phase === 'encoding' ? { ...p, ratio } : { phase: 'encoding', ratio, label: p.label || 'Encoding…' },
        );
      });

      setProgress({ phase: 'encoding', ratio: 0, label: 'Splicing…' });
      const data = await spliceClips(
        ffmpeg,
        clips.map((c) => c.file),
        preset,
        (label) => setProgress((p) => ({ ...p, phase: 'encoding', label })),
      );

      setProgress({ phase: 'finalizing', ratio: 1, label: 'Finalizing…' });
      // Copy into a fresh ArrayBuffer so the Blob owns its memory.
      const blob = new Blob([data.slice().buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      trackUrl(url);
      resultUrlRef.current = url;

      const totalDur = clips.reduce(
        (sum, c) => (c.durationSec != null ? sum + c.durationSec : sum),
        0,
      );
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      setResult({
        url,
        size: blob.size,
        filename: `spliced_${stamp}.mp4`,
        durationSec: totalDur > 0 ? totalDur : null,
      });
      setProgress({ phase: 'done', ratio: 1, label: 'Done' });
    } catch (err: any) {
      const msg = err?.message || String(err);
      setEngine((s) => (s === 'loading' ? 'error' : s));
      setEngineError(msg);
      setProgress({ phase: 'error', ratio: 0, label: msg });
    } finally {
      unsub?.();
    }
  }, [clips, preset, exporting, clearResult, trackUrl]);

  // ── Reset everything ───────────────────────────────────────────────────────
  const reset = useCallback(() => {
    clips.forEach((c) => revokeUrl(c.url));
    clearResult();
    setClips([]);
    setProgress({ phase: 'idle', ratio: 0, label: '' });
  }, [clips, revokeUrl, clearResult]);

  // ── Unmount cleanup — revoke any remaining object URLs ──────────────────────
  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      urls.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      });
      urls.clear();
    };
  }, []);

  const totalSize = clips.reduce((s, c) => s + c.size, 0);
  const totalDuration = clips.reduce(
    (s, c) => (c.durationSec != null ? s + c.durationSec : s),
    0,
  );

  return {
    // state
    clips,
    engine,
    engineError,
    preset,
    presets: RESOLUTION_PRESETS,
    progress,
    result,
    exporting,
    totalSize,
    totalDuration,
    // mutations
    addFiles,
    removeClip,
    moveClip,
    reorderClip,
    setPreset,
    exportVideo,
    clearResult,
    reset,
  };
}

export type UseVideoEditor = ReturnType<typeof useVideoEditor>;
